import { useQuery } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { AxiosError } from "axios";
import { Lock } from "lucide-react";
import { SpinnerBlock } from "@/components/ui/spinner";
import { NotFound } from "@/pages/NotFound";
import { ProgramPageRenderer } from "@/components/programPage/ProgramPageRenderer";
import { programPageService } from "@/services/programPage.service";

/**
 * A brand's program page, inside the affiliate shell. Same document a visitor
 * sees, plus the one thing only the affiliate can: where they stand on it.
 */
export default function AffiliateProgramPage() {
  const { username = "" } = useParams();
  const { data, isLoading, error } = useQuery({
    queryKey: ["program-page", "affiliate", username],
    queryFn: () => programPageService.getForAffiliate(username),
    enabled: !!username,
    retry: false,
  });

  if (isLoading) return <SpinnerBlock />;

  if (!data) {
    if ((error as AxiosError | null)?.response?.status === 403) {
      return (
        <div className="mx-auto flex max-w-md flex-col items-center gap-3 px-6 py-24 text-center">
          <Lock className="h-8 w-8 text-muted-foreground" />
          <h1 className="text-xl font-semibold">This page is for members</h1>
          <p className="text-muted-foreground">
            It opens up once this brand approves you as an affiliate.
          </p>
        </div>
      );
    }
    return <NotFound />;
  }

  return (
    <div className="px-0 lg:px-2">
      <ProgramPageRenderer
        document={data.document}
        programs={data.programs}
        defaultProgramId={data.defaultProgramId}
        standing={data.standing}
        // A join button means nothing to someone who is already in.
        joinUrl={data.isMember ? null : data.brand.joinUrl}
      />
    </div>
  );
}
